import React, { useState, useMemo, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform, TextInput, FlatList, Alert, ActivityIndicator } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign, MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { getModernCardStyle, getModernTextColor, getModernActionStyle } from "@stylesheet";
import { XStack, YStack, SizableText, H4, Avatar } from "tamagui";
import * as Animatable from 'react-native-animatable';
import apis, { getEmployees } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDeviceType } from '@hooks/useDeviceType';

interface User {
  id: string;
  name: string;
  role: string;
  email?: string;
  status: 'active' | 'inactive' | 'pending';
  imgSrc?: string;
  joinDate?: string;
  lastActive?: string;
  department?: string;
  _id?: string;
  isPending?: boolean;
  hrData?: {
    employeeId?: string;
    hireDate?: string;
    position?: string;
    salary?: number;
    benefits?: any[];
  };
}

const EmployeeManagement = () => {
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const queryClient = useQueryClient();
  const [employees, setEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchEmployees();
  }, [filterStatus]);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery]);

  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch employees using getEmployees API with status filter
      const params = filterStatus ? { status: filterStatus } : undefined;
      const response = await getEmployees(params);
      
      // Handle different response structures
      let employeesData = [];
      if (Array.isArray(response?.data)) {
        employeesData = response.data;
      } else if (Array.isArray(response)) {
        employeesData = response;
      } else if (response?.success && Array.isArray(response.data)) {
        employeesData = response.data;
      }

      // Ensure each employee has the correct structure
      const normalizedEmployees = employeesData.map((emp: any) => {
        // Handle both nested user structure and direct user object
        if (emp.user && emp.user._id) {
          return emp; // Already in correct format
        }
        // If it's a direct user object, wrap it
        if (emp._id && emp.profile_information) {
          return {
            user: emp,
            role: emp.role || 'employee',
            status: emp.status || 'active',
            department: emp.department,
            hrData: emp.hrData
          };
        }
        return emp;
      });

      setEmployees(normalizedEmployees);
    } catch (err: any) {
      console.error('❌ Error fetching employees:', err);
      setError(err.message || 'Failed to fetch employees');
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    // Filter by search query (status filtering is handled by API)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employee: any) => {
        const name = `${employee.user?.profile_information?.name || ''} ${employee.user?.profile_information?.surname || ''}`.trim().toLowerCase();
        const email = employee.user?.email?.toLowerCase() || '';
        const position = employee.hrData?.position?.toLowerCase() || '';
        const department = employee.department?.department_name?.toLowerCase() || '';
        return name.includes(query) || email.includes(query) || position.includes(query) || department.includes(query);
      });
    }

    // Sort: owners first, then by name
    filtered.sort((a, b) => {
      if (a.role === 'owner' && b.role !== 'owner') return -1;
      if (a.role !== 'owner' && b.role === 'owner') return 1;
      const nameA = `${a.user?.profile_information?.name || ''} ${a.user?.profile_information?.surname || ''}`.trim();
      const nameB = `${b.user?.profile_information?.name || ''} ${b.user?.profile_information?.surname || ''}`.trim();
      return nameA.localeCompare(nameB);
    });

    setFilteredEmployees(filtered);
  };

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
            Employee Management
          </Title>
        </View>

        {/* Search and Filter */}
        <View style={styles.searchContainer}>
          <View style={[
            styles.searchInput,
            { backgroundColor: theme ? '#f3f4f6' : '#374151' }
          ]}>
            <AntDesign name="search" size={20} color={theme ? '#6b7280' : '#9ca3af'} />
            <TextInput
              style={[styles.input, { color: theme ? '#111827' : '#f9fafb' }]}
              placeholder="Search employees..."
              placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <View style={styles.filterContainer}>
            <TouchableOpacity
              style={[
                styles.filterButton,
                filterStatus === undefined && { backgroundColor: '#3b82f6' }
              ]}
              onPress={() => setFilterStatus(undefined)}
            >
              <Text style={[styles.filterText, filterStatus === undefined && { color: '#ffffff' }]}>All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.filterButton,
                filterStatus === 'active' && { backgroundColor: '#10b981' }
              ]}
              onPress={() => setFilterStatus('active')}
            >
              <Text style={[styles.filterText, filterStatus === 'active' && { color: '#ffffff' }]}>Active</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.filterButton,
                filterStatus === 'inactive' && { backgroundColor: '#ef4444' }
              ]}
              onPress={() => setFilterStatus('inactive')}
            >
              <Text style={[styles.filterText, filterStatus === 'inactive' && { color: '#ffffff' }]}>Inactive</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.filterButton,
                filterStatus === 'pending' && { backgroundColor: '#f59e0b' }
              ]}
              onPress={() => setFilterStatus('pending')}
            >
              <Text style={[styles.filterText, filterStatus === 'pending' && { color: '#ffffff' }]}>Pending</Text>
            </TouchableOpacity>
          </View>
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
            <Text style={[styles.loadingText, { color: theme ? '#6b7280' : '#9ca3af', marginTop: 12 }]}>
              Loading employees...
            </Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <MaterialIcons name="error-outline" size={48} color="#ef4444" />
            <Text style={[styles.errorText, { color: theme ? '#111827' : '#f9fafb', marginTop: 12 }]}>
              {error}
            </Text>
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: '#3b82f6', marginTop: 16 }]}
              onPress={fetchEmployees}
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {filteredEmployees.map((employee: any) => {
              // Handle both nested and direct user structures
              const user = employee.user || employee;
              const employeeId = user?._id || employee._id || employee.id;
              const name = user?.profile_information?.name || user?.name || '';
              const surname = user?.profile_information?.surname || user?.surname || '';
              const email = user?.email || employee?.email || '';
              const status = employee.status || user?.status || 'active';
              const department = employee.department || user?.department;
              const hrData = employee.hrData || user?.hrData;
              const role = employee.role || user?.role || 'employee';

              return (
              <View
                key={employeeId}
                style={[
                  styles.employeeCard,
                  {
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderColor: theme ? '#e5e7eb' : '#374151'
                  }
                ]}
              >
                <View style={styles.employeeHeader}>
                  <View style={styles.employeeInfo}>
                    <Text style={[styles.employeeName, { color: theme ? '#111827' : '#f9fafb' }]}>
                      {name} {surname}
                    </Text>
                    {email && (
                      <Text style={[styles.employeeEmail, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        {email}
                      </Text>
                    )}
                    {role === 'owner' && (
                      <View style={[styles.ownerBadge, { backgroundColor: '#3b82f620' }]}>
                        <Text style={[styles.ownerText, { color: '#3b82f6' }]}>Owner</Text>
                      </View>
                    )}
                  </View>
                  <View style={[
                    styles.statusBadge,
                    {
                      backgroundColor: status === 'active'
                        ? '#10b98120'
                        : status === 'pending'
                          ? '#f59e0b20'
                          : '#ef444420'
                    }
                  ]}>
                    <Text style={[
                      styles.statusText,
                      {
                        color: status === 'active'
                          ? '#10b981'
                          : status === 'pending'
                            ? '#f59e0b'
                            : '#ef4444'
                      }
                    ]}>
                      {status || 'active'}
                    </Text>
                  </View>
                </View>
                {department && (
                  <View style={styles.departmentInfo}>
                    <MaterialCommunityIcons name="office-building" size={16} color={theme ? '#6b7280' : '#9ca3af'} />
                    <Text style={[styles.departmentText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      {typeof department === 'string' ? department : (department.department_name || department.name || 'No Department')}
                    </Text>
                  </View>
                )}
                {hrData && (
                  <View style={[
                    styles.hrDataContainer,
                    { borderTopColor: theme ? '#e5e7eb' : '#374151' }
                  ]}>
                    {hrData.position && (
                      <Text style={[styles.hrDataText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Position: {hrData.position}
                      </Text>
                    )}
                    {hrData.hireDate && (
                      <Text style={[styles.hrDataText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Hire Date: {new Date(hrData.hireDate).toLocaleDateString()}
                      </Text>
                    )}
                    {hrData.salary && (
                      <Text style={[styles.hrDataText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Salary: ${hrData.salary.toLocaleString()}
                      </Text>
                    )}
                  </View>
                )}
              </View>
            );
            })}
            {filteredEmployees.length === 0 && (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                  No employees found
                </Text>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  searchContainer: {
    marginBottom: 20
  },
  searchInput: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 12
  },
  input: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb'
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  loadingText: {
    fontSize: 16
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center'
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600'
  },
  employeeCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12
  },
  employeeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8
  },
  employeeInfo: {
    flex: 1
  },
  employeeName: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4
  },
  employeeEmail: {
    fontSize: 14
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  departmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8
  },
  departmentText: {
    fontSize: 14,
    marginLeft: 8
  },
  hrDataContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1
  },
  hrDataText: {
    fontSize: 12,
    marginBottom: 4
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center'
  },
  emptyText: {
    fontSize: 16
  },
  ownerBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginTop: 4,
    alignSelf: 'flex-start'
  },
  ownerText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase'
  }
});

export default EmployeeManagement;

